/**
 * Shared PII scrubbing for PostHog error capture (client, server, edge).
 *
 * Every `$exception` payload passes through `scrubPII` before leaving the
 * process — message, stack, and any extra context. Patterns drop values that
 * match email/token/JWT shapes; property keys in `PII_BLOCKLIST` are dropped
 * entirely.
 */

export const PII_BLOCKLIST: ReadonlySet<string> = new Set([
  "email",
  "password",
  "card",
  "card_number",
  "credit_card",
  "cvv",
  "ssn",
  "address",
  "phone",
  "phone_number",
  "ip",
  "ip_address",
  "secret",
  "token",
  "access_token",
  "refresh_token",
  "cookie",
  "authorization",
])

const EMAIL_PATTERN = /[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}/g
const SK_TOKEN_PATTERN = /sk[-_][A-Za-z0-9]{20,}/g
const PHX_TOKEN_PATTERN = /phx_[A-Za-z0-9]{20,}/g
// JWT base64url alphabet excludes `.` — use it as the segment separator only.
// Minimum 10 chars per segment catches real-world tokens (headers like
// `eyJhbGciOiJIUzI1NiJ9` are only 20 chars including the `eyJ` prefix).
const JWT_PATTERN = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g
const BEARER_PATTERN = /[Bb]earer\s+[A-Za-z0-9._-]{20,}/g

const REDACTED = "[REDACTED]"

export function scrubString(input: string): string {
  if (!input) return input
  return input
    .replace(EMAIL_PATTERN, REDACTED)
    .replace(JWT_PATTERN, REDACTED)
    .replace(BEARER_PATTERN, REDACTED)
    .replace(SK_TOKEN_PATTERN, REDACTED)
    .replace(PHX_TOKEN_PATTERN, REDACTED)
}

export function scrubProperties(
  props: Record<string, unknown>
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (PII_BLOCKLIST.has(key.toLowerCase())) continue
    if (value === undefined) continue
    if (typeof value === "string") {
      cleaned[key] = scrubString(value)
    } else if (value && typeof value === "object" && !Array.isArray(value)) {
      cleaned[key] = scrubProperties(value as Record<string, unknown>)
    } else {
      cleaned[key] = value
    }
  }
  return cleaned
}

/**
 * Build a fresh Error whose `message`, `stack`, and `name` are already scrubbed.
 * Pass the result — not the raw Error — to any SDK that auto-builds an
 * `$exception_list` from the Error object (posthog-node, posthog-js). Prevents
 * secrets or PII embedded in message/stack from reaching PostHog.
 */
export function buildScrubbedError(err: unknown): Error {
  let original: Error
  if (err instanceof Error) {
    original = err
  } else if (err !== null && typeof err === "object") {
    // Handles plain objects (Supabase PostgrestError, network errors, etc.) that
    // are not Error instances. Avoids `Error("[object Object]")` when the object
    // has no string `.message`.
    const errObj = err as {
      message?: unknown
      error?: unknown
      statusText?: unknown
      code?: unknown
      name?: unknown
      stack?: unknown
    }
    const altMessage =
      pickString(errObj.message) ??
      pickString(errObj.error) ??
      pickString(errObj.statusText) ??
      pickString(errObj.code) ??
      safeStringify(err)
    original = new Error(altMessage)
    original.name = typeof errObj.name === "string" ? errObj.name : "Error"
    if (typeof errObj.stack === "string") original.stack = errObj.stack
  } else {
    original = new Error(String(err))
  }
  const scrubbed = new Error(scrubString(original.message ?? ""))
  scrubbed.name = original.name || "Error"
  if (original.stack) {
    scrubbed.stack = scrubString(original.stack)
  }
  return scrubbed
}

function pickString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null
}

function safeStringify(value: unknown): string {
  try {
    const json = JSON.stringify(value)
    if (json && json !== "{}") return json
  } catch {
    // Circular reference or other JSON failure — fall through.
  }
  return "[unserializable error]"
}
